(() => {
  const number = value => Number.isFinite(Number(value)) ? Number(value) : 0;
  function packChoices(variants, size, closure, needed) {
    if (!Number.isInteger(needed) || needed < 1 || needed > 10000) return [];
    return variants.filter(v => v.available && v.size === size && (!closure || v.closure === closure) && v.bags > 0).map(v => {
      const minimum = Math.max(1, v.min);
      const step = Math.max(1, v.step);
      const raw = Math.max(minimum, Math.ceil(needed / v.bags));
      const quantity = minimum + Math.ceil((raw - minimum) / step) * step;
      return {...v, quantity, totalBags: quantity * v.bags, spare: quantity * v.bags - needed, total: quantity * v.price};
    }).filter(v => v.max === null || v.quantity <= v.max).sort((a, b) => a.total - b.total || a.spare - b.spare || a.quantity - b.quantity);
  }
  const toInches = dimension => {
    const unit = String(dimension?.unit || '').toLowerCase();
    const value = number(dimension?.value);
    if (['in', 'inches'].includes(unit)) return value;
    if (['cm', 'centimeters'].includes(unit)) return value / 2.54;
    if (['mm', 'millimeters'].includes(unit)) return value / 25.4;
    if (['m', 'meters'].includes(unit)) return value / 0.0254;
    return 0;
  };
  class PackingBench454 extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      if (!this.querySelector('[data-pb-copy]')) {
        document.addEventListener('DOMContentLoaded', () => this.connectedCallback(), {once:true});
        return;
      }
      this.ready = true;
      this.controller = new AbortController();
      this.host = this.closest('product-454');
      this.dialog = this.querySelector('dialog');
      this.copy = JSON.parse(this.querySelector('[data-pb-copy]').textContent);
      this.variants = [...this.querySelectorAll('[data-pb-variant]')].map(el => {
        const d = el.dataset;
        let dimensions = JSON.parse(d.dimensions || '[]');
        // International catalog uses the same two flat dimensions in its option values.
        if (dimensions.length < 2 && this.dataset.product === '9032111030529') {
          const sizes = {'11"x24"':[11,24], '15"x20"':[15,20]};
          if (sizes[d.size]) dimensions = sizes[d.size].map(value => ({value,unit:'in'}));
        }
        return {id:d.id, size:d.size, closure:d.closure, label:d.label, width:toInches(dimensions[0]), height:toInches(dimensions[1]), bags:number(d.bags), price:number(d.price), available:d.available === 'true', min:number(d.min), step:number(d.step), max:d.max === '' ? null : number(d.max)};
      }).filter(v => v.width > 0 && v.height > 0);
      if (!this.variants.length) return;
      this.money = new Intl.NumberFormat(this.dataset.locale || 'en', {style:'currency',currency:this.dataset.currency || 'USD',currencyDisplay:['de','nl','en-GB'].includes(this.dataset.locale) ? 'code' : 'symbol'});
      this.sizes = [...new Set(this.variants.map(v => v.size))];
      const selected = this.variants.find(v => v.id === this.dataset.variant) || this.variants[0];
      const saved = this.host?.packingBenchState;
      this.state = {size:selected.size, closure:'', needed:100, unit:this.dataset.unit, reference:['de','nl','en-GB'].includes(this.dataset.locale) ? 'a4' : 'letter', ...saved};
      if (!this.sizes.includes(this.state.size)) this.state.size = selected.size;
      this.querySelector('[data-pb-count]').value = this.state.needed;
      this.querySelector('[data-pb-closure]').value = this.state.closure;
      this.querySelector('[data-pb-reference]').value = this.state.reference;
      this.makeSizeButtons();
      this.addEventListener('click', event => this.click(event), {signal:this.controller.signal});
      this.addEventListener('change', event => this.change(event), {signal:this.controller.signal});
      this.querySelector('[data-pb-count]').addEventListener('input', event => this.change(event, false), {signal:this.controller.signal});
      this.dialog.addEventListener('close', () => this.unlock(), {signal:this.controller.signal});
      this.querySelector('[data-pb-open]').hidden = false;
      this.querySelector('[data-pb-fallback]').hidden = true;
      this.render();
    }
    disconnectedCallback() {
      this.controller?.abort();
      clearTimeout(this.announceTimer);
      this.unlock();
    }
    unlock() {
      if (this.previousOverflow !== undefined) {
        document.body.style.overflow = this.previousOverflow;
        this.previousOverflow = undefined;
      }
    }
    remember() { if (this.host) this.host.packingBenchState = {...this.state}; }
    format(key, values = {}) { return Object.entries(values).reduce((s,[k,v]) => s.replaceAll(`__${k}__`,String(v)), this.copy[key] || ''); }
    emit(action, detail = {}) {
      const data = {feature:'vacuum_packing_bench',action,product_id:this.dataset.product,...detail};
      this.dispatchEvent(new CustomEvent('454:packing-bench', {bubbles:true, detail:data}));
      try {
        if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true) {
          Promise.resolve(window.Shopify?.analytics?.publish?.('454_packing_bench',data)).catch(() => {});
        }
      } catch (_) {}
    }
    makeSizeButtons() {
      const wrap = this.querySelector('[data-pb-sizes]');
      wrap.replaceChildren();
      this.sizes.forEach(size => {
        const button = document.createElement('button');
        button.type = 'button'; button.dataset.pbSize = size;
        wrap.append(button);
      });
    }
    dimensions(v) {
      const factor = this.state.unit === 'cm' ? 2.54 : 1;
      const f = n => new Intl.NumberFormat(this.dataset.locale, {maximumFractionDigits:1}).format(n * factor);
      return `${f(v.width)} × ${f(v.height)} ${this.state.unit}`;
    }
    change(event, track = true) {
      const el = event.target;
      if (el.matches('[data-pb-count]')) this.state.needed = el.value === '' ? '' : Number(el.value);
      else if (el.matches('[data-pb-closure]')) this.state.closure = el.value;
      else if (el.matches('[data-pb-reference]')) this.state.reference = el.value;
      else return;
      this.remember(); this.render();
      if (track) this.emit('plan_changed', {bags_needed:Number(this.state.needed)||0,size:this.state.size});
    }
    click(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.matches('[data-pb-open]')) {
        this.dialog.showModal();
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.emit('opened');
      } else if (button.matches('[data-pb-close]')) this.dialog.close();
      else if (button.dataset.pbSize !== undefined) {
        this.state.size = button.dataset.pbSize;
        this.remember(); this.render(); this.emit('size_compared', {size:this.state.size});
      } else if (button.dataset.pbUnit) {
        this.state.unit = button.dataset.pbUnit; this.remember(); this.render();
      } else if (button.matches('[data-pb-reset]')) {
        const available = this.variants.find(v => v.available);
        if (available) {
          this.state.size = available.size; this.state.closure = '';
          this.querySelector('[data-pb-closure]').value = '';
          this.remember(); this.render();
        }
      } else if (button.dataset.pbApply) this.apply(button);
    }
    render() {
      const current = this.variants.find(v => v.size === this.state.size);
      const valid = Number.isInteger(this.state.needed) && this.state.needed >= 1 && this.state.needed <= 10000;
      this.querySelector('[data-pb-count]').setAttribute('aria-invalid',String(!valid));
      this.querySelectorAll('[data-pb-size]').forEach(button => {
        const v = this.variants.find(v => v.size === button.dataset.pbSize);
        button.textContent = this.dimensions(v);
        button.setAttribute('aria-pressed',String(button.dataset.pbSize === this.state.size));
      });
      this.querySelectorAll('[data-pb-unit]').forEach(button => button.setAttribute('aria-pressed',String(button.dataset.pbUnit === this.state.unit)));
      this.querySelector('[data-pb-dimension]').textContent = this.dimensions(current);
      const inStock = this.variants.some(v => v.available && v.size === this.state.size);
      const stock = this.querySelector('[data-pb-stock]');
      stock.textContent = inStock ? this.copy.available : this.copy.soldOut;
      stock.dataset.available = String(inStock);
      this.draw(current);
      const results = this.querySelector('[data-pb-results]');
      results.replaceChildren();
      this.choices = packChoices(this.variants,this.state.size,this.state.closure,this.state.needed);
      const message = this.querySelector('[data-pb-message]');
      message.textContent = !valid ? this.copy.invalid : !inStock ? this.copy.noSize : !this.choices.length ? this.copy.noQuantity : '';
      message.hidden = !message.textContent;
      this.querySelector('[data-pb-reset]').hidden = !valid || inStock || !this.variants.some(v => v.available);
      this.choices.forEach((choice,index) => {
        const card = document.createElement('article'); card.className = 'pb454-result';
        const top = document.createElement('div'); top.className = 'pb454-result-top';
        const name = document.createElement('strong'); name.textContent = this.format('packs',{quantity:choice.quantity,bags:choice.bags});
        const price = document.createElement('strong'); price.textContent = this.money.format(choice.total / 100);
        top.append(name,price);
        const label = document.createElement('p'); label.textContent = choice.label;
        const bottom = document.createElement('div'); bottom.className = 'pb454-result-bottom';
        const detail = document.createElement('span'); detail.textContent = this.format('coverage',{total:choice.totalBags,spare:choice.spare});
        const button = document.createElement('button'); button.type='button'; button.dataset.pbApply = choice.id; button.textContent = this.copy.use;
        button.setAttribute('aria-label',this.format('useLabel',{quantity:choice.quantity,bags:choice.bags,closure:choice.label}));
        bottom.append(detail,button); card.append(top,label,bottom);
        if (index === 0 && this.choices.length > 1) {
          const badge = document.createElement('small'); badge.className='pb454-best'; badge.textContent=this.copy.lowest; card.prepend(badge);
        }
        results.append(card);
      });
      clearTimeout(this.announceTimer);
      this.announceTimer = setTimeout(() => {this.querySelector('[data-pb-status]').textContent = message.textContent || this.format('results',{count:this.choices.length});},350);
    }
    draw(v) {
      const scale = 12.5, base = 380;
      const w=v.width*scale,h=v.height*scale,x=350-w/2,y=base-h;
      const set = (selector, attrs) => {const el=this.querySelector(selector); Object.entries(attrs).forEach(([k,val])=>el.setAttribute(k,val));};
      set('[data-pb-bag-group]',{transform:`translate(${x} ${y})`});
      set('[data-pb-bag]',{width:w,height:h});
      set('[data-pb-shine]',{d:`M 9 10 H ${w*.52} L ${w*.28} ${h-10} H 9 Z`});
      set('[data-pb-seam]',{x:7,y:7,width:w-14,height:h-14});
      set('[data-pb-seal]',{x1:7,x2:w-7,y1:18,y2:18});
      const logo = this.querySelector('[data-pb-logo]');
      if (logo) {const size=Math.min(w*.55,h*.45);const bounds={x:(w-size)/2,y:(h-size)/2,width:size,height:size};set('[data-pb-logo-bg]',bounds);set('[data-pb-logo]',{x:bounds.x+5,y:bounds.y+5,width:size-10,height:size-10});}
      else set('[data-pb-wordmark]',{x:w/2,y:h*.58});
      const zipper = this.state.closure && this.variants.some(p=>p.closure===this.state.closure && /zipper/i.test(p.closure) && !/no /i.test(p.closure));
      this.querySelector('[data-pb-seal]').style.opacity = zipper ? '1' : '.35';
      const paper=this.state.reference==='a4'?{w:210/25.4,h:297/25.4}:{w:8.5,h:11};
      set('[data-pb-paper]',{x:53,y:base-paper.h*scale,width:paper.w*scale,height:paper.h*scale});
      set('[data-pb-paper-label]',{x:53+paper.w*scale/2,y:base-paper.h*scale/2});
      this.querySelector('[data-pb-paper-label]').textContent=this.state.reference==='a4'?'A4':this.copy.letter;
      this.querySelector('[data-pb-paper-size]').textContent=this.dimensions({width:paper.w,height:paper.h});
      this.querySelector('[data-pb-visual-note]').textContent=this.format('visualNote',{size:this.dimensions(v)});
    }
    async apply(button) {
      const choice=this.choices.find(v=>v.id===button.dataset.pbApply);
      if (!choice || !this.host?.updateProduct) return;
      this.querySelectorAll('[data-pb-apply]').forEach(b=>b.disabled=true);
      this.emit('pack_selected',{variant_id:choice.id,quantity:choice.quantity,bags_needed:this.state.needed,total_bags:choice.totalBags,value:choice.total/100,currency:this.dataset.currency});
      this.remember();
      const host=this.host;
      const url=new URL(host.dataset.productUrl,location.origin);
      const current=new URL(location.href);
      ['preview_theme_id','_fd','pb','journeys_test'].forEach(k=>{if(current.searchParams.has(k))url.searchParams.set(k,current.searchParams.get(k));});
      url.searchParams.set('variant',choice.id);
      this.dialog.close();
      await host.updateProduct(url,null,true);
      const form=host.querySelector('.f454-form');
      if (!form || form.querySelector('[name="id"]')?.value!==choice.id) return;
      const input=form.querySelector('[name="quantity"]');
      input.value=choice.quantity;
      if (!input.checkValidity()) {input.reportValidity();input.focus();return;}
      input.dispatchEvent(new Event('change',{bubbles:true}));
      const plan=form.querySelector('[name="selling_plan"]');
      if (plan && [...plan.options].some(o=>o.value==='')) {plan.value='';plan.dispatchEvent(new Event('change',{bubbles:true}));}
      const status=form.querySelector('[data-form-status]');
      if (status) status.textContent=this.format('applied',{quantity:choice.quantity,bags:choice.totalBags});
      host.querySelector('.f454-purchase')?.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth',block:'start'});
      form.querySelector('[type="submit"]')?.focus({preventScroll:true});
    }
  }
  if (!customElements.get('packing-bench-454')) customElements.define('packing-bench-454',PackingBench454);
})();
